import { forwardRef } from 'react';
import { type VariantProps, cva } from 'class-variance-authority';

import classNames from '@/utils/classnames';

export const badgeVariants = cva(
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full font-medium',
  {
    variants: {
      variant: {
        neutral: 'bg-surface-3 text-fg-secondary',
        brand: 'bg-brand-tint text-brand-text',
        success: 'bg-success-tint text-success',
        warning: 'bg-warning-tint text-warning',
        danger: 'bg-danger-tint text-danger',
        info: 'bg-info-tint text-info',
      },
      size: {
        sm: 'h-5 px-2 text-2xs',
        md: 'h-6 px-2.5 text-xs',
      },
    },
    defaultVariants: {
      variant: 'neutral',
      size: 'sm',
    },
  },
);

const DOT_COLOR = {
  neutral: 'bg-fg-muted',
  brand: 'bg-brand',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
  info: 'bg-info',
} as const;

export interface BadgeProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'className'>, VariantProps<typeof badgeVariants> {
  className?: string;
  /** Render a leading status dot tinted to match the variant. */
  dot?: boolean;
}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ variant, size, dot, className, children, ...props }, ref) => (
    <span ref={ref} className={classNames(badgeVariants({ variant, size }), className)} {...props}>
      {dot && <span className={classNames('size-1.5 rounded-full', DOT_COLOR[variant ?? 'neutral'])} />}
      {children}
    </span>
  ),
);
Badge.displayName = 'Badge';
